import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "hl_admin_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((cfg) => {
  const token = getToken();
  if (token) {
    cfg.headers = cfg.headers || {};
    cfg.headers.Authorization = `Bearer ${token}`;
  }
  return cfg;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err?.response?.status === 401) {
      const url = err.config?.url || "";
      if (url.includes("/admin") || url.includes("/auth/")) {
        clearToken();
      }
    }
    return Promise.reject(err);
  }
);

// ----- Public -----
export const fetchGallery = async (params = {}) => (await api.get("/gallery", { params })).data;
export const fetchProjects = async () => (await api.get("/projects")).data;
export const fetchProject = async (id) => (await api.get(`/projects/${id}`)).data;
export const submitContact = async (payload) => (await api.post("/contact", payload)).data;

// ----- Auth -----
export const login = async (email, password) => {
  const { data } = await api.post("/auth/login", { email, password });
  setToken(data.access_token);
  return data.user;
};
export const fetchMe = async () => (await api.get("/auth/me")).data;
export const changePassword = async (current_password, new_password) =>
  (await api.post("/auth/change-password", { current_password, new_password })).data;

// ----- Admin: Gallery -----
export const adminListGallery = async (status) => {
  const params = status ? { status } : {};
  return (await api.get("/admin/gallery", { params })).data;
};
export const adminCreateGallery = async (payload) => (await api.post("/admin/gallery", payload)).data;
export const adminUpdateGallery = async (id, payload) => (await api.patch(`/admin/gallery/${id}`, payload)).data;
export const adminDeleteGallery = async (id) => (await api.delete(`/admin/gallery/${id}`)).data;
export const adminReorderGallery = async (items) =>
  (await api.post("/admin/gallery/reorder", { items })).data;
export const adminBulkGallery = async (ids, action) =>
  (await api.post("/admin/gallery/bulk", { ids, action })).data;
export const adminUpscaleGallery = async (id, scale = 2) =>
  (await api.post(`/admin/gallery/${id}/upscale`, { scale }, { timeout: 180000 })).data;
export const adminRemixGalleryAi = async (id) =>
  (await api.post(`/admin/gallery/${id}/remix-ai`, null, { timeout: 90000 })).data;
export const adminKitOptions = async () =>
  (await api.get("/admin/kit-options")).data;
export const adminBehindTheLens = async (id, { kit_used = null, corrections = [] } = {}) =>
  (await api.post(`/admin/gallery/${id}/behind-the-lens`, { kit_used, corrections }, { timeout: 90000 })).data;
export const adminBackfillTech = async ({ override = false } = {}) =>
  (await api.post("/admin/gallery/backfill-tech", { override })).data;

export const adminBackfillStatus = async (jobId) =>
  (await api.get(`/admin/gallery/backfill-tech/${jobId}/status`)).data;

// Poll backfill job - calls `onProgress(snapshot)` after every status fetch.
// Resolves with the final snapshot when status is completed, failed, or timeout.
export const adminPollBackfill = async (jobId, { onProgress, intervalMs = 2000, timeoutMs = 600000 } = {}) => {
  const start = Date.now();
  for (let tick = 0; tick < Math.ceil(timeoutMs / intervalMs); tick += 1) {
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, intervalMs));
    let st;
    try {
      // eslint-disable-next-line no-await-in-loop
      st = await adminBackfillStatus(jobId);
    } catch (e) {
      return { status: "disconnected", error: e?.message || "lost connection" };
    }
    if (onProgress) onProgress(st);
    if (st.status === "completed" || st.status === "failed") return st;
    if (Date.now() - start > timeoutMs) return { ...st, status: "timeout" };
  }
  return { status: "timeout" };
};
export const adminDetectKitItem = async (id) =>
  (await api.post(`/admin/gallery/${id}/detect-kit`, null, { timeout: 60000 })).data;
export const adminDetectKitUpload = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return (await api.post("/admin/detect-kit-upload", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 60000,
  })).data;
};
export const adminIntegrations = async () => (await api.get("/admin/integrations")).data;
export const adminVideoMetadata = async (url) =>
  (await api.post("/admin/video-metadata", { url }, { timeout: 60000 })).data;
export const adminVideoImport = async (urls, publish = false) =>
  (await api.post("/admin/gallery/video-import", { urls, publish }, { timeout: 120000 })).data;
export const adminVideoImportStatus = async (jobId) =>
  (await api.get(`/admin/gallery/video-import/${jobId}/status`)).data;
export const adminPollVideoImport = async (jobId, { onProgress, intervalMs = 2000, timeoutMs = 900000 } = {}) => {
  const start = Date.now();
  for (let tick = 0; tick < Math.ceil(timeoutMs / intervalMs); tick += 1) {
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, intervalMs));
    let st;
    try {
      // eslint-disable-next-line no-await-in-loop
      st = await adminVideoImportStatus(jobId);
    } catch (e) {
      return { status: "disconnected", error: e?.message || "lost connection" };
    }
    if (onProgress) onProgress(st);
    if (st.status === "completed" || st.status === "failed") return st;
    if (Date.now() - start > timeoutMs) return { ...st, status: "timeout" };
  }
  return { status: "timeout" };
};

// ----- Admin: Upload + AI -----
// Fast-path upload. Returns immediately with { success, imageId, status: "processing", url }.
// Frontend should poll adminUploadStatus until status becomes "ready" or "failed".
export const adminUploadImage = async (file, onProgress) => {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/admin/upload", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: onProgress,
    timeout: 60000,
  });
  return data;
};

export const adminUploadStatus = async (imageId) =>
  (await api.get(`/admin/upload/${imageId}/status`)).data;

// Poll wrapper - resolves when status is "ready" or "failed", or after timeoutMs.
export const adminWaitForUploadReady = async (imageId, { intervalMs = 2000, timeoutMs = 60000 } = {}) => {
  const start = Date.now();
  while (true) {
    const s = await adminUploadStatus(imageId);
    if (s.status === "ready" || s.status === "failed") return s;
    if (Date.now() - start > timeoutMs) return { ...s, status: "timeout" };
    await new Promise((r) => setTimeout(r, intervalMs));
  }
};

export const adminAnalyzeImage = async (file, extra = {}) => {
  const fd = new FormData();
  fd.append("file", file);
  if (extra.original_name) fd.append("original_name", extra.original_name);
  if (extra.client) fd.append("client", extra.client);
  if (extra.media_type) fd.append("media_type", extra.media_type);
  if (extra.remix) fd.append("remix", "true");
  const { data } = await api.post("/admin/analyze-image", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 90000,
  });
  return data;
};

// ----- Admin: Contacts -----
export const adminContacts = async () => (await api.get("/admin/contacts")).data;
export const adminMarkContact = async (id, read) => (await api.patch(`/admin/contacts/${id}`, { read })).data;
export const adminDeleteContact = async (id) => (await api.delete(`/admin/contacts/${id}`)).data;

// ----- Admin: Projects -----
export const adminListProjects = async () => (await api.get("/admin/projects")).data;
export const adminGetProject = async (id) => (await api.get(`/admin/projects/${id}`)).data;
export const adminCreateProject = async (payload) => (await api.post("/admin/projects", payload)).data;
export const adminUpdateProject = async (id, payload) => (await api.patch(`/admin/projects/${id}`, payload)).data;
export const adminDeleteProject = async (id) => (await api.delete(`/admin/projects/${id}`)).data;

// ----- Admin: Users -----
export const adminListUsers = async () => (await api.get("/admin/users")).data;
export const adminCreateUser = async (payload) => (await api.post("/admin/users", payload)).data;
export const adminUpdateUser = async (id, payload) => (await api.patch(`/admin/users/${id}`, payload)).data;
export const adminDeleteUser = async (id) => (await api.delete(`/admin/users/${id}`)).data;

// ----- Admin: Stats -----
export const adminStats = async () => (await api.get("/admin/stats")).data;

// Resolve image URL - pass through absolute URLs; prefix relative /api/files paths with backend
export const resolveMedia = (url) => {
  if (!url) return "";
  if (/^https?:\/\//.test(url)) return url;
  return `${BACKEND_URL}${url}`;
};
